/**
 * Monitoring Page Component
 * Displays scored articles and sentiment analytics for the client
 */

import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { auth } from '../auth';
import { Newspaper, BarChart3, ChevronDown, Share2, Copy, Mail, ExternalLink, Check, X, PenLine } from 'lucide-react';
import './Monitoring.css';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar, LabelList
} from 'recharts';

interface Article {
  id: number;
  title: string;
  url: string;
  source: string;
  source_type: 'news' | 'social' | 'unknown';
  published_date: string;
  heat_score: number;
  sentiment: 'Positive' | 'Neutral' | 'Negative';
  sentiment_score: number;
  alert_level: 'Low' | 'Medium' | 'High';
  content: string;
  summary: string;
  category?: string | null;
  matched_stakeholders?: unknown;
  creatorAttribution?: {
    label: string;
    values: string[];
    kind: 'individual' | 'source' | 'desk' | 'unclassified';
    provider: string | null;
  };
}

interface MonitoringSignal {
  type: 'coverage_rise' | 'negative_shift';
  tone: 'attention' | 'caution';
  title: string;
  message: string;
}

interface SourceStat {
  name: string;
  article_count: number;
  average_sentiment: number;
  sentiment: 'Positive' | 'Neutral' | 'Negative';
}

interface SourceStats {
  sources: SourceStat[];
  authors: SourceStat[];
}

interface Analytics {
  distribution: {
    Positive: number;
    Neutral: number;
    Negative: number;
  };
  total_articles: number;
  trend: Array<{
    date: string;
    Positive: number;
    Neutral: number;
    Negative: number;
  }>;
}

const Monitoring: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'articles' | 'analytics'>('articles');
  const [articles, setArticles] = useState<Article[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [signals, setSignals] = useState<MonitoringSignal[]>([]);
  const [dismissedSignalTypes, setDismissedSignalTypes] = useState<Set<MonitoringSignal['type']>>(new Set());
  const [sourceStats, setSourceStats] = useState<SourceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [timePeriod, setTimePeriod] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceType, setSourceType] = useState<'all' | 'news' | 'social'>('all');
  const [stakeholderFilter, setStakeholderFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState<'published_date' | 'heat_score' | 'sentiment'>('published_date');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // UI state
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const sourceDropdownRef = useRef<HTMLDivElement>(null);

  // Batch 1: article sharing and explicit multi-select state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedArticleIds, setSelectedArticleIds] = useState<Set<number>>(new Set());
  const [shareMenuArticleId, setShareMenuArticleId] = useState<number | null>(null);
  const [showBulkShareMenu, setShowBulkShareMenu] = useState(false);
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  const shareTriggerRef = useRef<HTMLButtonElement>(null);
  const bulkShareMenuRef = useRef<HTMLDivElement>(null);
  const bulkShareTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false);
      }
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(event.target as Node)) {
        setShowSourceDropdown(false);
      }
      const target = event.target as Node;
      const withinArticleShareMenu = shareMenuRef.current?.contains(target);
      const withinBulkShareMenu = bulkShareMenuRef.current?.contains(target);
      if (!withinArticleShareMenu && !withinBulkShareMenu) {
        setShareMenuArticleId(null);
        setShowBulkShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeTab === 'articles') {
      setOffset(0);
      setArticles([]);
      setSelectionMode(false);
      setSelectedArticleIds(new Set());
      setShareMenuArticleId(null);
      setShowBulkShareMenu(false);
      fetchArticles(0);
    }
  }, [activeTab, timePeriod, searchTerm, sourceType, sortBy, sortOrder]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics();
      fetchSourceStats();
    }
  }, [activeTab, timePeriod]);

  useEffect(() => {
    if (activeTab === 'articles') {
      fetchSignals();
    }
  }, [activeTab, timePeriod]);

  useEffect(() => {
    setSelectionMode(false);
    setSelectedArticleIds(new Set());
  }, [stakeholderFilter, categoryFilter]);

  useEffect(() => {
    if (!shareMenuArticleId && !showBulkShareMenu) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const shouldReturnToBulkTrigger = showBulkShareMenu;
        setShareMenuArticleId(null);
        setShowBulkShareMenu(false);
        window.setTimeout(() => {
          (shouldReturnToBulkTrigger ? bulkShareTriggerRef : shareTriggerRef).current?.focus();
        }, 0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shareMenuArticleId, showBulkShareMenu]);

  useEffect(() => {
    if (selectedArticleIds.size === 0) {
      setShowBulkShareMenu(false);
    }
  }, [selectedArticleIds]);

  useEffect(() => {
    if (!shareFeedback) return;
    const timer = window.setTimeout(() => setShareFeedback(null), 3200);
    return () => window.clearTimeout(timer);
  }, [shareFeedback]);

  const fetchArticles = async (pageOffset: number = 0) => {
    try {
      if (pageOffset === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      const data = await api.getMonitoringArticles(timePeriod, searchTerm, sourceType, sortBy, sortOrder, pageOffset, limit);
      if (pageOffset === 0) {
        setArticles(data.articles || []);
      } else {
        setArticles(prev => [...prev, ...(data.articles || [])]);
      }
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
      setOffset(pageOffset + limit);
    } catch (err: any) {
      setError(err.message || "Failed to load articles");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchSignals = async () => {
    try {
      const data = await api.getMonitoringSignals();
      setSignals(data.signals || []);
    } catch {
      // Signals are additive: do not block the established Monitoring feed if unavailable.
      setSignals([]);
    }
  };

  const visibleSignals = signals.filter(signal => !dismissedSignalTypes.has(signal.type));

  const dismissSignal = (signalType: MonitoringSignal['type']) => {
    setDismissedSignalTypes(previous => new Set(previous).add(signalType));
  };

  const fetchSourceStats = async () => {
    try {
      const data = await api.getMonitoringSourceStats(timePeriod);
      setSourceStats({
        sources: data.sources || [],
        authors: data.authors || []
      });
    } catch {
      // Source intelligence is additive: preserve the existing Analytics view if unavailable.
      setSourceStats(null);
    }
  };

  const isTouchPrimaryDevice = () => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches
  );

  const buildArticleDigest = (articleList: Article[]) => articleList
    .map((article, index) => `${index + 1}. ${article.title}\n${article.source} · ${formatDate(article.published_date)}\n${article.url}`)
    .join('\n\n');

  const copyTextToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textArea);
      return copied;
    } catch {
      return false;
    }
  };

  const toggleSelectionMode = () => {
    if (selectionMode) {
      setSelectedArticleIds(new Set());
    }
    setSelectionMode(!selectionMode);
    setShareMenuArticleId(null);
    setShowBulkShareMenu(false);
  };

  const toggleArticleSelection = (articleId: number) => {
    setSelectedArticleIds(previous => {
      const next = new Set(previous);
      if (next.has(articleId)) {
        next.delete(articleId);
      } else {
        next.add(articleId);
      }
      return next;
    });
  };

  const handleSingleShare = async (article: Article, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    shareTriggerRef.current = event.currentTarget;

    if (isTouchPrimaryDevice() && navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: buildArticleDigest([article])
        });
        setShareFeedback('Article shared.');
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          setShareFeedback('Unable to open sharing options.');
        }
      }
      return;
    }

    setShowBulkShareMenu(false);
    setShareMenuArticleId(current => current === article.id ? null : article.id);
  };

  const handleCopyArticleLink = async (article: Article) => {
    const copied = await copyTextToClipboard(article.url);
    setShareMenuArticleId(null);
    setShareFeedback(copied ? 'Link copied.' : 'Unable to copy the link.');
  };

  const handleEmailArticle = (article: Article) => {
    const subject = encodeURIComponent(article.title);
    const body = encodeURIComponent(buildArticleDigest([article]));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShareMenuArticleId(null);
  };

  const handleOpenArticle = (article: Article) => {
    window.open(article.url, '_blank', 'noopener,noreferrer');
    setShareMenuArticleId(null);
  };

  const getSelectedArticles = () => articles.filter(article => selectedArticleIds.has(article.id));

  const handleBulkShare = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const selectedArticles = getSelectedArticles();
    if (!selectedArticles.length) return;

    bulkShareTriggerRef.current = event.currentTarget;
    const digest = buildArticleDigest(selectedArticles);
    if (isTouchPrimaryDevice() && navigator.share) {
      try {
        await navigator.share({
          title: `${selectedArticles.length} monitoring article${selectedArticles.length === 1 ? '' : 's'}`,
          text: digest
        });
        setShareFeedback('Selected articles shared.');
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          setShareFeedback('Unable to open sharing options.');
        }
      }
      return;
    }

    setShareMenuArticleId(null);
    setShowBulkShareMenu(current => !current);
  };

  const handleCopySelectedArticles = async () => {
    const copied = await copyTextToClipboard(buildArticleDigest(getSelectedArticles()));
    setShowBulkShareMenu(false);
    setShareFeedback(copied ? 'Selected article links copied.' : 'Unable to copy the selected article links.');
  };

  const handleEmailSelectedArticles = () => {
    const selectedArticles = getSelectedArticles();
    const subject = encodeURIComponent(`${selectedArticles.length} selected monitoring article${selectedArticles.length === 1 ? '' : 's'}`);
    const body = encodeURIComponent(buildArticleDigest(selectedArticles));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShowBulkShareMenu(false);
  };

  const handleShowMore = () => {
    fetchArticles(offset);
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getMonitoringAnalytics(timePeriod);
      setAnalytics(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      
      // Build query parameters from current filters
      const params = new URLSearchParams();
      params.append('time_period', timePeriod === 'all' ? 'all' : `${timePeriod}d`);
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (sourceType !== 'all') {
        params.append('source_type', sourceType);
      }
      
      // Build headers with auth and impersonation for admin users
      const headers: Record<string, string> = {
        ...auth.getAuthHeader()
      };
      
      // For admin users, add the impersonated client ID header
      if (auth.isAdminPortalUser()) {
        const selectedClientId = auth.getClientId();
        if (selectedClientId) {
          headers['X-Impersonated-Client-ID'] = selectedClientId;
        }
      }
      
      // Make API request
      const response = await fetch(
        `/api/portal/monitoring/export/csv?${params}`,
        { headers }
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monitoring_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export monitoring data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportingPdf(true);
      
      // Build query parameters from current filters
      const params = new URLSearchParams();
      params.append('time_period', timePeriod === 'all' ? 'all' : `${timePeriod}d`);
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (sourceType !== 'all') {
        params.append('source_type', sourceType);
      }
      
      // Build headers with auth and impersonation for admin users
      const headers: Record<string, string> = {
        ...auth.getAuthHeader()
      };
      
      // For admin users, add the impersonated client ID header
      if (auth.isAdminPortalUser()) {
        const selectedClientId = auth.getClientId();
        if (selectedClientId) {
          headers['X-Impersonated-Client-ID'] = selectedClientId;
        }
      }
      
      // Make API request
      const response = await fetch(
        `/api/portal/monitoring/export/pdf?${params}`,
        { headers }
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monitoring_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('PDF Export failed:', error);
      alert('Failed to export monitoring PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const getSentimentClass = (sentiment: string) => {
    switch (sentiment) {
      case 'Positive': return 'sentiment-positive';
      case 'Negative': return 'sentiment-negative';
      default: return 'sentiment-neutral';
    }
  };

  const getAlertClass = (alert: string) => {
    switch (alert) {
      case 'High': return 'alert-high';
      case 'Medium': return 'alert-medium';
      default: return 'alert-low';
    }
  };

  const getHeatScoreClass = (score: number) => {
    if (score >= 7) return 'heat-high';
    if (score >= 4) return 'heat-medium';
    return 'heat-low';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStakeholderNames = (article: Article): string[] => {
    let parsed = article.matched_stakeholders;

    // Older rows may contain a JSON-encoded string while newer rows return a JSON array.
    for (let attempt = 0; attempt < 2 && typeof parsed === 'string'; attempt += 1) {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return [];
      }
    }

    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((stakeholder: any) => typeof stakeholder === 'string' ? stakeholder : stakeholder?.name)
      .filter((name): name is string => typeof name === 'string' && name.trim().length > 0);
  };

  const stakeholderOptions = Array.from(
    new Set(articles.flatMap(getStakeholderNames))
  ).sort((left, right) => left.localeCompare(right));

  const categoryOptions = Array.from(
    new Set(articles
      .map(article => article.category?.trim())
      .filter((category): category is string => Boolean(category)))
  ).sort((left, right) => left.localeCompare(right));

  const visibleArticles = articles.filter(article => {
    const matchesStakeholder = !stakeholderFilter || getStakeholderNames(article).includes(stakeholderFilter);
    const matchesCategory = !categoryFilter || article.category?.trim() === categoryFilter;
    return matchesStakeholder && matchesCategory;
  });

  const clearDiscoveryFilters = () => {
    setStakeholderFilter('');
    setCategoryFilter('');
  };

  const isArticlePublishedToday = (article: Article) => {
    const publishedDate = new Date(article.published_date);
    const currentDate = new Date();

    return publishedDate.getFullYear() === currentDate.getFullYear()
      && publishedDate.getMonth() === currentDate.getMonth()
      && publishedDate.getDate() === currentDate.getDate();
  };

  const groupArticlesByWeek = (articles: Article[]) => {
    const grouped: { [key: string]: Article[] } = {};
    
    articles.forEach(article => {
      const date = new Date(article.published_date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
      
      if (!grouped[weekKey]) {
        grouped[weekKey] = [];
      }
      grouped[weekKey].push(article);
    });
    
    return grouped;
  };

  const COLORS = {
    Positive: '#10b981',
    Neutral: '#64748b',
    Negative: '#ef4444'
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const total = analytics?.total_articles || 1;
      const value = payload[0].value;
      const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
      
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '12px',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p style={{ fontWeight: 500, margin: 0 }}>{payload[0].name}</p>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
            {value} articles ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        style={{ fontWeight: 600, fontSize: '14px' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const renderSentimentChart = () => {
    if (!analytics) return null;
    
    const { distribution, total_articles } = analytics;
    
    // Transform data for Recharts
    const chartData = [
      { name: 'Positive', value: distribution.Positive, color: COLORS.Positive },
      { name: 'Neutral', value: distribution.Neutral, color: COLORS.Neutral },
      { name: 'Negative', value: distribution.Negative, color: COLORS.Negative }
    ];
    
    return (
      <div className="sentiment-chart">
        <h3>Sentiment Distribution</h3>
        <p className="chart-subtitle">Overall sentiment breakdown for the selected period</p>
        
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              innerRadius={60}
              outerRadius={100}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              formatter={(value: string, entry: any) => (
                <span style={{ fontSize: '14px' }}>
                  {value}: {entry.payload.value} articles
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderTrendChart = () => {
    if (!analytics || !analytics.trend.length) return null;
    
    return (
      <div className="trend-chart">
        <h3>Sentiment Trend Over Time</h3>
        <p className="chart-subtitle">Daily sentiment distribution showing patterns and changes</p>
        
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={analytics.trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #e5e7eb',
                borderRadius: '6px'
              }}
            />
            <Legend 
              wrapperStyle={{ fontSize: '14px' }}
            />
            <Line 
              type="monotone" 
              dataKey="Positive" 
              stroke={COLORS.Positive} 
              strokeWidth={2}
              name="Positive"
              dot={{ fill: COLORS.Positive, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="Neutral" 
              stroke={COLORS.Neutral} 
              strokeWidth={2}
              name="Neutral"
              dot={{ fill: COLORS.Neutral, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="Negative" 
              stroke={COLORS.Negative} 
              strokeWidth={2}
              name="Negative"
              dot={{ fill: COLORS.Negative, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderSourceBarChart = (
    entries: SourceStat[],
    itemLabel: 'article' | 'post',
    chartLabel: string
  ) => (
    <>
      <div className="source-intelligence-chart" role="img" aria-label={chartLabel}>
        <ResponsiveContainer width="100%" height={Math.max(220, entries.length * 46)}>
          <BarChart
            data={entries}
            layout="vertical"
            margin={{ top: 4, right: 48, left: 12, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} stroke="#6b7280" />
            <YAxis
              type="category"
              dataKey="name"
              width={156}
              tick={{ fontSize: 12, fill: '#374151' }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(234, 88, 12, 0.07)' }}
              content={({ active, payload }) => {
                const source = payload?.[0]?.payload as SourceStat | undefined;
                if (!active || !source) return null;
                return (
                  <div className="source-intelligence-tooltip">
                    <strong>{source.name}</strong>
                    <span>{source.article_count} {itemLabel}{source.article_count === 1 ? '' : 's'}</span>
                    <span className={`source-intelligence-sentiment ${source.sentiment.toLowerCase()}`}>
                      {source.sentiment} tone
                    </span>
                  </div>
                );
              }}
            />
            <Bar dataKey="article_count" name={itemLabel === 'article' ? 'Articles' : 'Posts'} fill="#ea580c" radius={[0, 5, 5, 0]}>
              <LabelList dataKey="article_count" position="right" fill="#374151" fontSize={12} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ul className="source-intelligence-accessible-list">
        {entries.map(entry => (
          <li key={entry.name}>{entry.name}: {entry.article_count} {itemLabel}{entry.article_count === 1 ? '' : 's'}, {entry.sentiment} tone.</li>
        ))}
      </ul>
    </>
  );

  const renderSourceIntelligence = () => {
    if (!sourceStats || (!sourceStats.sources.length && !sourceStats.authors.length)) return null;

    return (
      <section className="source-intelligence" aria-labelledby="source-intelligence-heading">
        <div className="source-intelligence-heading">
          <div>
            <h3 id="source-intelligence-heading">Coverage Landscape</h3>
            <p>Which publications and monitored social voices are generating the most coverage in the selected period.</p>
          </div>
        </div>
        {sourceStats.sources.length > 0 && (
          <div className="source-intelligence-group">
            <h4>Coverage by Publication</h4>
            <p className="source-intelligence-description">Bar length shows article volume. Hover over or tap a bar to see the publication's average tone.</p>
            {renderSourceBarChart(sourceStats.sources, 'article', 'Publication coverage by article volume')}
          </div>
        )}
        <div className="source-intelligence-group">
          <h4>Top Social Voices</h4>
          {sourceStats.authors.length > 0 ? (
            <>
              <p className="source-intelligence-description">Monitored social voices ranked by post volume in the selected period.</p>
              {renderSourceBarChart(sourceStats.authors, 'post', 'Social voice coverage by post volume')}
            </>
          ) : (
            <p className="source-intelligence-empty">No monitored social voices appeared in this period. Select a longer period to explore historic activity.</p>
          )}
        </div>
      </section>
    );
  };

  const todayArticles = timePeriod === '1'
    ? visibleArticles
    : visibleArticles.filter(isArticlePublishedToday);
  const olderArticles = timePeriod === '1'
    ? []
    : visibleArticles.filter(article => !isArticlePublishedToday(article));
  const articleGroups = [
    ...(todayArticles.length > 0 ? [{
      key: 'today',
      title: timePeriod === '1' ? 'Today — last 24 hours' : 'Today',
      articles: todayArticles,
      isToday: true
    }] : []),
    ...Object.entries(groupArticlesByWeek(olderArticles)).map(([week, weekArticles]) => ({
      key: `week-${week}`,
      title: `Week of ${week}`,
      articles: weekArticles,
      isToday: false
    }))
  ];

  return (
    <div className="monitoring-page">
      <div className="monitoring-header">
        <div>
          <h1>Monitoring</h1>
          <p className="monitoring-subtitle">Real-time article tracking with HEAT scores and sentiment analysis</p>
        </div>
        {activeTab === 'articles' && (
          <div className="export-buttons-group">
            <button 
              className="export-csv-btn"
              onClick={handleExportCSV}
              disabled={exporting || articles.length === 0}
              title="Export articles to CSV"
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <button 
              className="export-pdf-btn"
              onClick={handleExportPDF}
              disabled={exportingPdf || articles.length === 0}
              title="Export articles to PDF"
            >
              {exportingPdf ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="monitoring-tabs">
        <button 
          className={`tab-button ${activeTab === 'articles' ? 'active' : ''}`}
          onClick={() => setActiveTab('articles')}
          data-tour="monitoring-articles-tab"
        >
          <Newspaper size={18} style={{ marginRight: '8px' }} /> Scored Articles
        </button>
        <button 
          className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
          data-tour="monitoring-analytics-tab"
        >
          <BarChart3 size={18} style={{ marginRight: '8px' }} /> Analytics
        </button>
      </div>

      {/* Filters */}
      <div className="monitoring-filters">
        {activeTab === 'articles' && (
          <>
            <input
              type="text"
              placeholder="Search articles by keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            
            <div className="filter-dropdown" ref={sortDropdownRef}>
              <button 
                className="dropdown-trigger"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
              >
                Sort: {sortBy === 'published_date' ? 'Time' : sortBy === 'heat_score' ? 'HEAT Score' : 'Sentiment'}
                <ChevronDown size={16} style={{ marginLeft: '4px' }} />
              </button>
              {showSortDropdown && (
                <div className="dropdown-menu">
                  <button 
                    className={`dropdown-item ${sortBy === 'published_date' ? 'active' : ''}`}
                    onClick={() => {
                      setSortBy('published_date');
                      setShowSortDropdown(false);
                    }}
                  >
                    Time {sortBy === 'published_date' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </button>
                  <button 
                    className={`dropdown-item ${sortBy === 'heat_score' ? 'active' : ''}`}
                    onClick={() => {
                      setSortBy('heat_score');
                      setShowSortDropdown(false);
                    }}
                  >
                    HEAT Score {sortBy === 'heat_score' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </button>
                  <button 
                    className={`dropdown-item ${sortBy === 'sentiment' ? 'active' : ''}`}
                    onClick={() => {
                      setSortBy('sentiment');
                      setShowSortDropdown(false);
                    }}
                  >
                    Sentiment {sortBy === 'sentiment' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </button>
                  <div className="dropdown-divider"></div>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                      setShowSortDropdown(false);
                    }}
                  >
                    Reverse Order
                  </button>
                </div>
              )}
            </div>

            <div className="filter-dropdown" ref={sourceDropdownRef}>
              <button 
                className="dropdown-trigger"
                onClick={() => setShowSourceDropdown(!showSourceDropdown)}
              >
                Source: {sourceType === 'all' ? 'All' : sourceType === 'news' ? 'News' : 'Social'}
                <ChevronDown size={16} style={{ marginLeft: '4px' }} />
              </button>
              {showSourceDropdown && (
                <div className="dropdown-menu">
                  <button 
                    className={`dropdown-item ${sourceType === 'all' ? 'active' : ''}`}
                    onClick={() => {
                      setSourceType('all');
                      setShowSourceDropdown(false);
                    }}
                  >
                    All Sources
                  </button>
                  <button 
                    className={`dropdown-item ${sourceType === 'news' ? 'active' : ''}`}
                    onClick={() => {
                      setSourceType('news');
                      setShowSourceDropdown(false);
                    }}
                  >
                    News Only
                  </button>
                  <button 
                    className={`dropdown-item ${sourceType === 'social' ? 'active' : ''}`}
                    onClick={() => {
                      setSourceType('social');
                      setShowSourceDropdown(false);
                    }}
                  >
                    Social Signals Only
                  </button>
                </div>
              )}
            </div>

            <select
              className="monitoring-discovery-select"
              value={stakeholderFilter}
              onChange={(event) => setStakeholderFilter(event.target.value)}
              aria-label="Filter articles by stakeholder"
            >
              <option value="">Stakeholder: All</option>
              {stakeholderOptions.map(stakeholder => (
                <option key={stakeholder} value={stakeholder}>{stakeholder}</option>
              ))}
            </select>

            <select
              className="monitoring-discovery-select"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
              aria-label="Filter articles by topic"
            >
              <option value="">Topic: All</option>
              {categoryOptions.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            {(stakeholderFilter || categoryFilter) && (
              <button type="button" className="monitoring-clear-discovery-button" onClick={clearDiscoveryFilters}>
                Clear discovery filters
              </button>
            )}

            <button
              type="button"
              className={`monitoring-select-button ${selectionMode ? 'active' : ''}`}
              onClick={toggleSelectionMode}
              aria-pressed={selectionMode}
            >
              {selectionMode ? <Check size={16} aria-hidden="true" /> : <Share2 size={16} aria-hidden="true" />}
              {selectionMode ? 'Selection active' : 'Select'}
            </button>
          </>
        )}
        
        <div className="time-period-filters">
          <button 
            className={`period-button ${timePeriod === '1' ? 'active' : ''}`}
            onClick={() => setTimePeriod('1')}
          >
            Today
          </button>
          <button 
            className={`period-button ${timePeriod === '7' ? 'active' : ''}`}
            onClick={() => setTimePeriod('7')}
          >
            Last 7 Days
          </button>
          <button 
            className={`period-button ${timePeriod === '30' ? 'active' : ''}`}
            onClick={() => setTimePeriod('30')}
          >
            Last 30 Days
          </button>
          <button 
            className={`period-button ${timePeriod === 'all' ? 'active' : ''}`}
            onClick={() => setTimePeriod('all')}
          >
            All Time
          </button>
          {activeTab === 'articles' && timePeriod === '1' && (
            <p className="today-period-note" role="status">Showing coverage published in the last 24 hours.</p>
          )}
        </div>
      </div>

      {activeTab === 'articles' && visibleSignals.length > 0 && (
        <section className="monitoring-signals" aria-label="What matters right now">
          <div className="monitoring-signals-heading">
            <h2>Right Now</h2>
            <p>Current changes in coverage that may warrant attention.</p>
          </div>
          <div className="monitoring-signals-list">
            {visibleSignals.map(signal => (
              <article key={signal.type} className={`monitoring-signal-card ${signal.tone}`}>
                <div className="monitoring-signal-card-heading">
                  <strong>{signal.title}</strong>
                  <button
                    type="button"
                    className="monitoring-signal-dismiss"
                    onClick={() => dismissSignal(signal.type)}
                    aria-label={`Dismiss ${signal.title} for this session`}
                    title="Dismiss for this session"
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                </div>
                <span>{signal.message}</span>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Content */}
      <div className="monitoring-content">
        {loading && <div className="loading">Loading...</div>}
        {error && <div className="error">{error}</div>}
        
        {!loading && !error && activeTab === 'articles' && (
          <div className="articles-list">
            <p className="articles-count">
              Showing {visibleArticles.length} of {total} articles
              {(stakeholderFilter || categoryFilter) && ' matching discovery filters'}
            </p>
            {shareFeedback && (
              <p className="monitoring-share-feedback" role="status" aria-live="polite">
                {shareFeedback}
              </p>
            )}
            
            {articleGroups.map(articleGroup => (
              <div key={articleGroup.key} className={`week-group ${articleGroup.isToday ? 'today-group' : ''}`}>
                <h3 className={`week-header ${articleGroup.isToday ? 'today-header' : ''}`}>{articleGroup.title}</h3>
                
                {articleGroup.articles.map(article => (
                  <div key={article.id} className={`article-card ${selectionMode ? 'selection-mode' : ''}`}>
                    {selectionMode && (
                      <label className="article-selection-control" onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedArticleIds.has(article.id)}
                          onChange={() => toggleArticleSelection(article.id)}
                          aria-label={`Select ${article.title}`}
                        />
                      </label>
                    )}
                    <div className="article-date">
                      {formatDate(article.published_date)}
                    </div>
                    
                    <div className="article-content">
                      <a href={article.url} target="_blank" rel="noopener noreferrer" className="article-title">
                        {article.title}
                      </a>
                      {article.creatorAttribution && (
                        <p className={`article-creator-attribution ${article.creatorAttribution.kind}`}>
                          <PenLine size={14} aria-hidden="true" />
                          <span>{article.creatorAttribution.label}</span>
                        </p>
                      )}
                      <p className="article-excerpt">{article.summary}</p>
                    </div>
                    
                    <div className="article-meta">
                      <span className={`heat-score ${getHeatScoreClass(article.heat_score)}`}>
                        {article.heat_score.toFixed(1)}
                      </span>
                      <span className={`sentiment-badge ${getSentimentClass(article.sentiment)}`}>
                        {article.sentiment}
                      </span>
                      <span className={`alert-badge ${getAlertClass(article.alert_level)}`}>
                        {article.alert_level}
                      </span>
                      <span className="article-source">{article.source}</span>
                      <span className="article-source-type">
                        {article.source_type === 'news' ? '📰' : article.source_type === 'social' ? '𝕏' : ''}
                      </span>
                      <div
                        className="monitoring-share-control"
                        ref={shareMenuArticleId === article.id ? shareMenuRef : null}
                      >
                        <button
                          type="button"
                          className="monitoring-share-button"
                          onClick={(event) => handleSingleShare(article, event)}
                          aria-label={`Share ${article.title}`}
                          aria-expanded={shareMenuArticleId === article.id}
                          aria-haspopup="menu"
                        >
                          <Share2 size={16} aria-hidden="true" />
                          <span>Share</span>
                        </button>
                        {shareMenuArticleId === article.id && (
                          <div className="monitoring-share-menu" role="menu" aria-label={`Sharing options for ${article.title}`}>
                            <button type="button" role="menuitem" onClick={() => handleCopyArticleLink(article)}>
                              <Copy size={15} aria-hidden="true" /> Copy link
                            </button>
                            <button type="button" role="menuitem" onClick={() => handleEmailArticle(article)}>
                              <Mail size={15} aria-hidden="true" /> Share by email
                            </button>
                            <button type="button" role="menuitem" onClick={() => handleOpenArticle(article)}>
                              <ExternalLink size={15} aria-hidden="true" /> Open article
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            
            {visibleArticles.length === 0 && (
              <div className="empty-state">
                <p>{stakeholderFilter || categoryFilter ? 'No articles match the selected discovery filters.' : 'No articles found for the selected period.'}</p>
                {(stakeholderFilter || categoryFilter) && (
                  <button type="button" className="monitoring-empty-clear-button" onClick={clearDiscoveryFilters}>
                    Clear discovery filters
                  </button>
                )}
              </div>
            )}

            {hasMore && (
              <div className="show-more-container">
                <button 
                  className="show-more-btn"
                  onClick={handleShowMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading more...' : 'Show More'}
                </button>
              </div>
            )}

            {selectionMode && selectedArticleIds.size > 0 && (
              <div className="monitoring-selection-action-bar" role="region" aria-label="Selected article actions">
                <span>{selectedArticleIds.size} selected</span>
                <div className="monitoring-share-control" ref={bulkShareMenuRef}>
                  <button
                    ref={bulkShareTriggerRef}
                    type="button"
                    className="monitoring-selection-share"
                    onClick={handleBulkShare}
                    aria-haspopup="menu"
                    aria-expanded={showBulkShareMenu}
                  >
                    <Share2 size={16} aria-hidden="true" />
                    Share {selectedArticleIds.size} article{selectedArticleIds.size === 1 ? '' : 's'}
                  </button>
                  {showBulkShareMenu && (
                    <div className="monitoring-share-menu" role="menu" style={{ top: 'auto', bottom: 'calc(100% + 6px)' }}>
                      <button type="button" role="menuitem" onClick={handleCopySelectedArticles}>
                        <Copy size={15} aria-hidden="true" /> Copy links
                      </button>
                      <button type="button" role="menuitem" onClick={handleEmailSelectedArticles}>
                        <Mail size={15} aria-hidden="true" /> Email links
                      </button>
                    </div>
                  )}
                </div>
                <button type="button" className="monitoring-selection-cancel" onClick={toggleSelectionMode}>
                  <X size={16} aria-hidden="true" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
        
        {!loading && !error && activeTab === 'analytics' && analytics && (
          <div className="analytics-view">
            {renderSentimentChart()}
            {renderTrendChart()}
            {renderSourceIntelligence()}
          </div>
        )}
      </div>
    </div>
  );
};

export default Monitoring;
